// 화면 그리기(§9.8.2~9.8.3) — 편집기 ↔ 전달 수단 연결. **뷰어와 원격지 그림판이 같은 코드**를 쓴다(역할만 다르다).
//
// 전달 수단(`DrawLink`)은 둘이다: 뷰어 = `draw` DataChannel(channel.ts) · 원격지 = 통합앱 이벤트/명령(그림판 창).
// 역할 차이:
//   - viewer: 켜고 끄는 주체. 켜면 `on` → 원격지가 그림판을 열고 `caps`·`sync` 로 답한다.
//   - remote: **문서 상태의 주인**(§9.8.2). `on`·`syncreq` 를 받으면 전체 상태(`sync`)를 보낸다.
// DOM 없음(시험 대상) — 두 편집기를 메모리 링크로 이어 양방향을 시험한다.
import { DrawEditor } from './editor.ts';
import type { Author } from './model.ts';
import { DrawOutbox, isOp } from './protocol.ts';
import type { DrawMsg } from './protocol.ts';

/** 메시지를 보내고 받는 수단. 받기는 `setListener` 로 한 곳에 연결한다. */
export interface DrawLink {
  send(text: string): void;
  setListener(fn: ((m: DrawMsg) => void) | null): void;
}

export type DrawRole = 'viewer' | 'remote';

export interface DrawSyncEvents {
  /** 상대가 그리기를 껐다(뷰어: 원격지 사람이 끔 / 원격지: 뷰어가 끔·세션 끝). */
  onRemoteOff?: () => void;
  /** 원격지 그림판이 열렸다(뷰어에게 — 켜기 요청이 도달했다는 확인). */
  onRemoteReady?: (excluded: boolean) => void;
  /** 상대가 그리는 중인가(마우스 주인 표시). */
  onPeerBusy?: (busy: boolean) => void;
  /** 뷰어가 그리기를 켜 달라고 했다(원격지 전용 — 앱이 그림판 창을 연다). */
  onPeerOn?: () => void;
}

export class DrawSync {
  readonly editor: DrawEditor;
  readonly role: DrawRole;
  private readonly outbox: DrawOutbox;
  private readonly link: DrawLink;
  private readonly events: DrawSyncEvents;
  /** 원격지 그림이 영상에서 빠지는가(뷰어 전용). false 면 원격지 작성분을 캔버스에 그리지 않는다. */
  excluded = true;

  constructor(role: DrawRole, link: DrawLink, events: DrawSyncEvents = {}, onChange?: () => void) {
    this.role = role;
    this.link = link;
    this.events = events;
    this.outbox = new DrawOutbox((t) => link.send(t));
    const me: Author = role === 'viewer' ? 'v' : 'r';
    this.editor = new DrawEditor(me, {
      onChange,
      send: (op) => this.outbox.send(op),
      sendLaser: (p) => this.outbox.send({ t: 'laser', by: me, p: [p[0], p[1]] }),
    });
    link.setListener((m) => this.receive(m));
  }

  get author(): Author {
    return this.editor.author;
  }

  /** 켜기 알림(뷰어). 원격지는 그림판을 열고 `caps`·`sync` 로 답한다. */
  announceOn(): void {
    this.outbox.send({ t: 'on', by: this.author });
  }

  /** 끄기 알림 + 내 문서 비우기(어느 쪽이든 — 끄면 양쪽 그림이 모두 사라진다, §9.8.3). */
  announceOff(): void {
    this.outbox.send({ t: 'off', by: this.author });
    this.editor.reset();
  }

  /** 원격지: 그림판이 뜬 뒤 능력·전체 상태를 보낸다. */
  sendReady(excluded: boolean): void {
    this.outbox.send({ t: 'caps', excluded });
    this.sendSync();
  }

  /** 뷰어: 전체 상태를 다시 달라(재접속·채널 재수립). */
  requestSync(): void {
    this.outbox.send({ t: 'syncreq' });
  }

  sendSync(): void {
    this.outbox.send({ t: 'sync', ...this.editor.doc.snapshot() });
  }

  /** 마우스 주인 표시(원격지 사람이 그리기 도구를 잡았다/놓았다). */
  sendBusy(on: boolean): void {
    this.outbox.send({ t: 'busy', by: this.author, on });
  }

  /** 남은 묶음을 바로 보낸다(창을 닫기 직전 등). */
  flush(): void {
    this.outbox.flush();
  }

  detach(): void {
    this.outbox.flush();
    this.link.setListener(null);
  }

  private receive(m: DrawMsg): void {
    if (isOp(m)) {
      // 상대의 `begin` 은 상대 이름으로만 — 내 이름을 단 획을 만들어 내 되돌리기와 섞이지 않게.
      if (m.t === 'begin' && m.obj.by === this.author) return;
      this.editor.applyRemote(m);
      return;
    }
    switch (m.t) {
      case 'laser':
        if (m.by !== this.author) this.editor.remoteLaser(m.by, m.p);
        break;
      case 'sync':
        // 뷰어만 받는다 — 문서의 주인은 원격지다(원격지가 뷰어 상태로 덮어쓰면 원격지 사람 그림이 사라질 수 있다).
        if (this.role === 'viewer') this.editor.loadSync({ objs: m.objs, board: m.board });
        break;
      case 'syncreq':
        if (this.role === 'remote') this.sendSync();
        break;
      case 'on':
        if (this.role === 'remote' && m.by === 'v') this.events.onPeerOn?.();
        break;
      case 'off':
        if (m.by !== this.author) {
          this.editor.reset();
          this.events.onRemoteOff?.();
        }
        break;
      case 'caps':
        if (this.role === 'viewer') {
          this.excluded = m.excluded;
          this.events.onRemoteReady?.(m.excluded);
        }
        break;
      case 'busy':
        if (m.by !== this.author) this.events.onPeerBusy?.(m.on);
        break;
      case 'hello':
        break; // 채널 능력 — 채널 쪽이 처리한다
    }
  }
}
